import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { And, NumberEquals, Rule, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { shareReplay, take } from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';
import { RulePath } from '../consts';
import * as CloseLostReasonFuncs from '../close-lost-reason';
import { CloseLostReason } from '../close-lost-reason';
import * as OpportunityStatusFuncs from '../opportunity-status';
import { OpportunityStatus } from '../opportunity-status';
import * as LastEditActionConditionFuncs from '../last-edit-action-condition';
import { LastEditActionCondition } from '../last-edit-action-condition';
import { CommonModule } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { SalesOpportunitiesService } from '../../../../../data-services/sales-opportunities.service';
import { PipelineSelectorComponent } from '../../../common/shared-form-elements';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';

@Component({
  selector: 'automata-opportunity-updated-panel',
  styleUrls: ['./opportunity-updated-panel.component.scss'],
  templateUrl: './opportunity-updated-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyInputModule,
    PipelineSelectorComponent,
    AutomationReplacementChipsComponent,
  ],
})
export class OpportunityUpdatedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  @ViewChild('pipelineSelector', { static: false }) pipelineSelector: PipelineSelectorComponent;
  OpportunityStatus = OpportunityStatus;
  LastEditActionCondition = LastEditActionCondition;

  lastEditActionConditionControl: FormControl;
  closedPipelineControl: FormControl;
  opportunityStatusControl: FormControl;
  closedLostReasonControl: FormControl;
  isBuiltInAutomation = false;

  opportunityStatusOptions = new Map<string, string>([
    [OpportunityStatus.ClosedAny, CloseLostReasonFuncs.I18NKey(CloseLostReason.AnyStar)],
    [OpportunityStatus.ClosedWon, OpportunityStatusFuncs.I18NKey(OpportunityStatus.ClosedWon)],
    [OpportunityStatus.ClosedLost, OpportunityStatusFuncs.I18NKey(OpportunityStatus.ClosedLost)],
  ]);

  closedLostReasonOptions = new Map<string, string>([
    [CloseLostReason.AnyStar, CloseLostReasonFuncs.I18NKey(CloseLostReason.AnyStar)],
    [CloseLostReason.Price, CloseLostReasonFuncs.I18NKey(CloseLostReason.Price)],
    [CloseLostReason.NoBudget, CloseLostReasonFuncs.I18NKey(CloseLostReason.NoBudget)],
    [CloseLostReason.LostToCompetitor, CloseLostReasonFuncs.I18NKey(CloseLostReason.LostToCompetitor)],
    [CloseLostReason.NotReady, CloseLostReasonFuncs.I18NKey(CloseLostReason.NotReady)],
    [CloseLostReason.Other, CloseLostReasonFuncs.I18NKey(CloseLostReason.Other)],
  ]);

  lastEditActionConditionOptions = new Map<string, string>([
    [LastEditActionCondition.Create, 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.LAST_EDIT_ACTION_OPTIONS.CREATED'],
    [
      LastEditActionCondition.UpdateProbability,
      'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.LAST_EDIT_ACTION_OPTIONS.CHANGED_STAGE',
    ],
    [
      LastEditActionCondition.CreatedOrUpdated,
      'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.LAST_EDIT_ACTION_OPTIONS.CREATED_OR_CHANGED_STAGE',
    ],
    [LastEditActionCondition.Closed, 'AUTOMATIONS.EDITOR.TRIGGERS.OPPORTUNITY.LAST_EDIT_ACTION_OPTIONS.CLOSED'],
  ]);

  opportunityStatusDataService = new StaticSelectorService<string>(
    this.opportunityStatusOptions,
    this.translateService,
  );
  closedLostReasonDataService = new StaticSelectorService<string>(this.closedLostReasonOptions, this.translateService);
  lastEditActionConditionDataService = new StaticSelectorService<string>(
    this.lastEditActionConditionOptions,
    this.translateService,
  );

  disablePipelineAndStageSelection$ = this.automationService
    .isNonPartnerAutomation()
    .pipe(take(1), shareReplay({ bufferSize: 1, refCount: true }));

  initialPipelineId = '';
  initialStageId: number | undefined = undefined;

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly translateService: TranslateService,
    protected readonly salesOpportunitiesService: SalesOpportunitiesService,
  ) {
    super();
    this.lastEditActionConditionControl = new FormControl(LastEditActionCondition.Closed);
    this.closedPipelineControl = new FormControl(null);
    this.opportunityStatusControl = new FormControl(OpportunityStatus.ClosedAny, Validators.required);
    this.closedLostReasonControl = new FormControl(CloseLostReason.AnyStar, Validators.required);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.savingEnabled$.next(true);
    if (this.triggerChoices?.rules?.length <= 0) {
      return;
    }

    const rule = Rule.fromRuleInterface(this.triggerChoices.rules[0]) as And;

    const lastEditActionConditionRule = Rule.fromRuleInterface(rule.value[0]);
    let lastEditActionCondition = (lastEditActionConditionRule.extractValue() ||
      LastEditActionCondition.Closed) as LastEditActionCondition;
    if (Array.isArray(lastEditActionCondition)) {
      lastEditActionCondition = LastEditActionCondition.CreatedOrUpdated;
    }

    const opportunityStatusRule = Rule.fromRuleInterface(rule.value[1]);
    let opportunityStatus = (opportunityStatusRule.extractValue() || OpportunityStatus.ClosedAny) as OpportunityStatus;
    if (Array.isArray(opportunityStatus)) {
      opportunityStatus = OpportunityStatus.ClosedAny;
    }

    const pipelineId = rule.listValuesByVariablePath(RulePath.pipelineIdPath)[0] || null;

    const probability: number = rule.listValuesByVariablePath(RulePath.probabilityPath)[0];

    const closedReason = rule.listValuesByVariablePath(RulePath.closedLostReasonPath)[0] || CloseLostReason.AnyStar;

    this.lastEditActionConditionControl.setValue(lastEditActionCondition);
    this.closedPipelineControl.setValue(pipelineId);
    this.opportunityStatusControl.setValue(opportunityStatus);
    this.closedLostReasonControl.setValue(closedReason);
    this.initialPipelineId = pipelineId as string;
    this.initialStageId = probability;
  }

  getControlsToValidateOnSave(): FormControl[] {
    let result: FormControl[] = [
      this.lastEditActionConditionControl,
      this.closedLostReasonControl,
      this.opportunityStatusControl,
    ];
    if (this.pipelineSelector) {
      result.push(...this.pipelineSelector.getControlsToValidateOnSave());
    }
    if (!this.isBuiltInAutomation && this.lastEditActionConditionControl.value === LastEditActionCondition.Closed) {
      result = result.concat(this.closedPipelineControl);
    }
    return result;
  }

  isFormValid(): boolean {
    let valid = !this.opportunityStatusControl.invalid;
    if (this.lastEditActionConditionControl.value === LastEditActionCondition.Closed) {
      valid =
        valid && !this.closedPipelineControl.invalid && this.opportunityStatusControl.value !== OpportunityStatus.Open;
      if (this.opportunityStatusControl.value === OpportunityStatus.ClosedLost) {
        valid = valid && !this.closedLostReasonControl.invalid;
      }
    }
    if (this.pipelineSelector) {
      valid = valid && this.pipelineSelector.isFormValid();
    }
    return valid;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    return this.setAllRules();
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    const baseRules = this.setAllRules();
    baseRules[0].next = rules[0].next;

    return baseRules;
  }

  setAllRules(): RuleInterface[] {
    // Header Rule
    const rules = [];
    const lastEditActionCondition: LastEditActionCondition = this.lastEditActionConditionControl.value;
    rules.push(LastEditActionConditionFuncs.getRule(lastEditActionCondition));

    // Determining which status rule to set
    const status = this.getOpportunityStatus(lastEditActionCondition);
    rules.push(OpportunityStatusFuncs.getRule(status));

    if (status === OpportunityStatus.Open && !this.isBuiltInAutomation) {
      const selectedPipeline = this.pipelineSelector?.getSelectedPipeline();
      if (selectedPipeline?.pipelineId) {
        rules.push(new StringEquals(RulePath.pipelineIdPath, selectedPipeline.pipelineId, ''));
        rules.push(new NumberEquals(RulePath.probabilityPath, selectedPipeline.stageId, ''));
      }
    }
    if (status !== OpportunityStatus.Open && !this.isBuiltInAutomation) {
      if (this.closedPipelineControl.value) {
        rules.push(new StringEquals(RulePath.pipelineIdPath, this.closedPipelineControl.value, ''));
      }
    }

    if (status === OpportunityStatus.ClosedLost && this.closedLostReasonControl.value !== CloseLostReason.AnyStar) {
      rules.push(new StringEquals(RulePath.closedLostReasonPath, this.closedLostReasonControl.value, ''));
    }

    const and = new And(rules, '');
    return [and.toRuleInterface()];
  }

  private getOpportunityStatus(lastEditActionCondition: LastEditActionCondition): OpportunityStatus {
    // Determining which status rule to set
    let status = OpportunityStatus.Open;
    if (lastEditActionCondition === LastEditActionCondition.Closed && !!this.opportunityStatusControl.value) {
      status = this.opportunityStatusControl.value as OpportunityStatus;
    }
    return status;
  }
}
